import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { dataHookFactory, initMapFactory } from "../common/reducers/utilities";
import { ensureArray } from "../common/utils/arrayUtilities";
import { createEagerSelector, eagerMetaFactory } from "../common/reducers/pendingUpdateSlice";

const epcrProcedureSlice = createSlice({
    name: 'epcrProcedures',
    initialState: {
        data: {},
        defs: []
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const procedure of ensureArray(action.payload)) {
                if (!state.data[procedure.pcrid]) {
                    state.data[procedure.pcrid] = [procedure];
                } else {
                    let idx = state.data[procedure.pcrid].findIndex(a => procedure.id === a.id);
                    if (idx >= 0) {
                        state.data[procedure.pcrid].splice(idx, 1, procedure);
                    } else {
                        state.data[procedure.pcrid] = [...state.data[procedure.pcrid], procedure];
                    }
                }
            }
        },
        remove: (state, action) => {
            for (const procedure of ensureArray(action.payload)) {
                let idx = state.data[procedure.pcrid].findIndex(a => a.id === procedure.id);
                if (idx >= 0) {
                    state.data[procedure.pcrid].splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => epcrProcedureSlice.getInitialState());
    }
});

// #region network operations
const eagerMeta = eagerMetaFactory(epcrProcedureSlice.name);
const fetchEpcrProcedures = pcrid => network => network.get('/api/v1/epcrprocedures/' + encodeURIComponent(pcrid), epcrProcedureSlice.actions.addOrUpdate);
export const addEpcrProcedure = (network, data) => network.put('/api/v1/epcrprocedure', data, epcrProcedureSlice.actions.addOrUpdate, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
export const removeEpcrProcedure = (network, data) => network.delete('/api/v1/epcrprocedure/' + encodeURIComponent(data.id), epcrProcedureSlice.actions.remove, eagerMeta(data.id, { fk: data.pcrid })); // TODO better failure
const fetchEpcrProcedureDefs = (network) => network.get('/api/v1/epcrprocedures', epcrProcedureSlice.actions.setDefs);
// #endregion

// #region selectors
const selectEpcrProcedureDefs = state => state.epcrProcedures.defs;
const selectAllEpcrProcedures = state => state.epcrProcedures.data;

const eagerSelector = createEagerSelector(epcrProcedureSlice.name, { mode: 'mapToArray' });
const selectEpcrProceduresByID = eagerSelector(createSelector([selectAllEpcrProcedures, (procedures, pcrid) => pcrid], (procedures, pcrid) => procedures[pcrid] ?? []));
const selectEpcrProcedures = pcrid  => state => selectEpcrProceduresByID(state, pcrid);
// #endregion

// #region listeners
const listen = {
    epcrProcedureAdded: (procedure, { dispatch }) => dispatch(epcrProcedureSlice.actions.addOrUpdate(procedure)),
    epcrProcedureRemoved: (procedure, { dispatch }) => dispatch(epcrProcedureSlice.actions.remove(procedure))
}
// #endregion

export const [useEpcrProcedureDefs,realizeEpcrProcedureDefs] = dataHookFactory(selectEpcrProcedureDefs, { run: fetchEpcrProcedureDefs });
export const [useEpcrProdcedures,realizeEpcrProcedures] = dataHookFactory(selectEpcrProcedures, initMapFactory(fetchEpcrProcedures, listen, pcrid => pcrid?.toString()));

const reducerDetails = { name: epcrProcedureSlice.name, reducer: epcrProcedureSlice.reducer };
export default reducerDetails;

